(() => {
  'use strict';
  if (window.AWEcosystemP1Bridge?.version) return;

  const KEYS = {
    replicas: 'aw_garage_replicas_v1',
    active: 'aw_garage_active_replica_v1',
    context: 'aw_airsoft_context_v1',
    legacyPlatform: 'aw_selected_platform',
    legacyManufacturer: 'aw_selected_manufacturer',
    legacyType: 'aw_selected_replica_type'
  };
  const URLS = {
    garage: '/pages/mon-atelier-airsoft',
    compatibility: '/pages/compatibilite-airsoft',
    atelier: '/pages/notre-atelier-de-preparation-de-repliques-airsoft#aw-atelier-prestations'
  };
  const normalize = (value = '') => String(value || '').trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/&/g, ' et ').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const readJson = (key, fallback) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      return JSON.parse(raw) ?? fallback;
    } catch (_) { return fallback; }
  };
  const writeJson = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); return true; }
    catch (_) { return false; }
  };
  const writeText = (key, value) => {
    if (!value) return;
    try { localStorage.setItem(key, String(value)); } catch (_) {}
  };
  const list = (value = '') => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  const replicas = () => {
    const value = readJson(KEYS.replicas, []);
    return Array.isArray(value) ? value : [];
  };
  const activeId = () => String(readJson(KEYS.active, '') || '').trim();
  const activeReplica = () => {
    const id = activeId();
    return id ? replicas().find((replica) => String(replica?.id || '') === id) || null : null;
  };
  const modelNodes = () => Array.from(document.querySelectorAll('[data-aw-tool-model],[data-aw-model-context-model]'));
  const modelRecord = (node) => node ? {
    handle: String(node.dataset.handle || '').trim(),
    label: String(node.dataset.label || '').trim(),
    model: String(node.dataset.model || '').trim(),
    manufacturer: String(node.dataset.manufacturer || '').trim(),
    platform: String(node.dataset.platform || '').trim(),
    platformLabel: String(node.dataset.platformLabel || '').trim(),
    type: String(node.dataset.type || '').trim(),
    pageUrl: String(node.dataset.pageUrl || '').trim(),
    workshopServices: list(node.dataset.workshopServices)
  } : null;

  const resolveExactModel = (replica) => {
    if (!replica) return null;
    const manufacturer = normalize(replica.manufacturer);
    const model = normalize(replica.model);
    const platform = normalize(replica.platform);
    const type = normalize(replica.type);
    const full = normalize([replica.manufacturer, replica.model].filter(Boolean).join(' '));
    if (!model && !full) return null;

    const matches = modelNodes().map(modelRecord).filter(Boolean).filter((record) => {
      if (manufacturer && normalize(record.manufacturer) !== manufacturer) return false;
      if (platform && record.platform && normalize(record.platform) !== platform) return false;
      if (type && record.type && normalize(record.type) !== type) return false;
      const recordModel = normalize(record.model);
      const recordLabel = normalize(record.label);
      return Boolean((model && recordModel === model) || (full && recordLabel === full));
    });
    const unique = new Map(matches.map((record) => [
      record.handle || `${normalize(record.manufacturer)}|${normalize(record.model)}|${normalize(record.platform)}|${normalize(record.type)}`,
      record
    ]));
    return unique.size === 1 ? [...unique.values()][0] : null;
  };

  const compatibilityUrl = (replica = activeReplica()) => {
    const record = resolveExactModel(replica);
    if (!record) return URLS.compatibility;
    const url = new URL(URLS.compatibility, location.origin);
    url.searchParams.set('aw_model', record.label || record.model);
    url.searchParams.set('aw_from', 'garage');
    return `${url.pathname}${url.search}`;
  };

  const injectCompatibilityModels = () => {
    const records = modelNodes().map(modelRecord).filter((record) => record?.model && record?.manufacturer && record?.platform);
    if (!records.length) return 0;
    let added = 0;
    document.querySelectorAll('[data-aw-compatibility-search]').forEach((root) => {
      const source = root.querySelector('[data-aw-model-source]');
      if (!source) return;
      const signatures = new Set(Array.from(source.querySelectorAll('[data-aw-model-data]')).map((node) => [
        normalize(node.dataset.manufacturer), normalize(node.dataset.model),
        normalize(node.dataset.platform), normalize(node.dataset.replicaType)
      ].join('|')));
      records.forEach((record) => {
        const signature = [
          normalize(record.manufacturer), normalize(record.model),
          normalize(record.platform), normalize(record.type)
        ].join('|');
        if (signatures.has(signature)) return;
        const node = document.createElement('span');
        node.dataset.awModelData = '';
        node.dataset.awEcosystemModel = 'true';
        node.dataset.id = record.handle || normalize(`${record.manufacturer}-${record.model}-${record.platform}-${record.type}`);
        node.dataset.model = record.model;
        node.dataset.manufacturer = record.manufacturer;
        node.dataset.platform = normalize(record.platform);
        node.dataset.platformLabel = record.platformLabel || record.platform;
        node.dataset.replicaType = normalize(record.type);
        node.dataset.replicaTypeLabel = record.type || 'Tous';
        node.dataset.keywords = record.label || '';
        node.dataset.seoUrl = record.pageUrl || '';
        source.append(node);
        signatures.add(signature);
        added += 1;
      });
    });
    return added;
  };

  const renderCompatibilityCoverage = () => {
    document.querySelectorAll('[data-aw-compatibility-search]').forEach((root) => {
      const models = Array.from(root.querySelectorAll('[data-aw-model-data]'));
      const signatures = new Set(models.map((node) => [
        normalize(node.dataset.manufacturer), normalize(node.dataset.model),
        normalize(node.dataset.platform), normalize(node.dataset.replicaType)
      ].join('|')).filter((signature) => signature.replace(/\|/g, '')));
      const dedicatedPages = new Set(modelNodes()
        .filter((node) => String(node.dataset.pageUrl || '').trim())
        .map((node) => String(node.dataset.pageUrl || '').trim()));
      let note = root.querySelector('[data-aw-ecosystem-compat-coverage]');
      if (!note) {
        note = document.createElement('p');
        note.dataset.awEcosystemCompatCoverage = 'true';
        note.className = 'aw-ecosystem-p1-coverage';
        root.querySelector('.aw-compatibility-search__finder')?.prepend(note);
      }
      if (!note) return;
      note.textContent = `Base technique AW : ${signatures.size} profil${signatures.size > 1 ? 's' : ''} exact${signatures.size > 1 ? 's' : ''} recherchable${signatures.size > 1 ? 's' : ''} · ${dedicatedPages.size} fiche${dedicatedPages.size > 1 ? 's' : ''} dédiée${dedicatedPages.size > 1 ? 's' : ''}.`;
    });
  };

  const modelLabel = (replica) => {
    const manufacturer = String(replica?.manufacturer || '').trim();
    const model = String(replica?.model || '').trim();
    const nickname = String(replica?.nickname || '').trim();
    return [manufacturer, model].filter(Boolean).join(' ').trim() || nickname || 'Réplique Garage';
  };
  const contextDetail = (replica) => ({
    platform: String(replica?.platform || '').trim(),
    platformLabel: String(replica?.platform || '').trim(),
    manufacturer: String(replica?.manufacturer || '').trim(),
    manufacturerLabel: String(replica?.manufacturer || '').trim(),
    type: String(replica?.type || '').trim(),
    typeLabel: String(replica?.type || '').trim(),
    model: String(replica?.model || '').trim(),
    modelLabel: modelLabel(replica)
  });
  const writeFallbackContext = (detail) => {
    const next = {
      version: 1,
      platform: normalize(detail.platform),
      platformLabel: detail.platformLabel || '',
      manufacturer: normalize(detail.manufacturer),
      manufacturerLabel: detail.manufacturerLabel || '',
      type: normalize(detail.type),
      typeLabel: detail.typeLabel || '',
      model: detail.model || '',
      modelLabel: detail.modelLabel || '',
      source: 'garage-active',
      updatedAt: new Date().toISOString()
    };
    writeJson(KEYS.context, next);
    writeText(KEYS.legacyPlatform, next.platform);
    writeText(KEYS.legacyManufacturer, next.manufacturer);
    writeText(KEYS.legacyType, next.type);
    window.dispatchEvent(new CustomEvent('aw:context-changed', { detail: next }));
    return next;
  };
  const syncReplica = (replica, source = 'garage-active') => {
    if (!replica) return null;
    const detail = contextDetail(replica);
    const context = window.AWAirsoftContext?.write
      ? window.AWAirsoftContext.write(detail, source)
      : writeFallbackContext(detail);
    window.dispatchEvent(new CustomEvent('aw:garage-context-changed', { detail: { replica, context, source } }));
    return context;
  };
  const activate = (id, source = 'garage-active') => {
    const replica = replicas().find((item) => String(item?.id || '') === String(id || ''));
    if (!replica) return null;
    writeJson(KEYS.active, String(replica.id));
    syncReplica(replica, source);
    render();
    return replica;
  };
  const syncActive = (source = 'garage-active-refresh') => {
    const replica = activeReplica();
    if (replica) syncReplica(replica, source);
    render();
    return replica;
  };
  const buildLink = (label, href, className = '') => {
    const link = document.createElement('a');
    link.href = href;
    link.textContent = label;
    link.className = className;
    return link;
  };

  const renderContextStrip = () => {
    document.querySelectorAll('.aw14-nav').forEach((nav) => {
      let strip = nav.nextElementSibling;
      if (!strip?.matches?.('[data-aw-ecosystem-context]')) {
        strip = document.createElement('aside');
        strip.dataset.awEcosystemContext = 'true';
        strip.className = 'aw-ecosystem-p1-context';
        nav.insertAdjacentElement('afterend', strip);
      }
      const replica = activeReplica();
      strip.replaceChildren();
      const copy = document.createElement('div');
      copy.className = 'aw-ecosystem-p1-context__copy';
      const eyebrow = document.createElement('span');
      eyebrow.className = 'aw-ecosystem-p1-context__eyebrow';
      const title = document.createElement('strong');
      title.className = 'aw-ecosystem-p1-context__title';
      const meta = document.createElement('span');
      meta.className = 'aw-ecosystem-p1-context__meta';
      if (replica) {
        eyebrow.textContent = 'Réplique active';
        title.textContent = modelLabel(replica);
        const record = resolveExactModel(replica);
        const details = [replica.platform, replica.type].map((value) => String(value || '').trim()).filter(Boolean);
        if (record) details.push('Modèle exact AW reconnu');
        if (record?.workshopServices?.length) details.push(`${record.workshopServices.length} prestation${record.workshopServices.length > 1 ? 's' : ''} Atelier liée${record.workshopServices.length > 1 ? 's' : ''}`);
        meta.textContent = details.length ? details.join(' · ') : 'Contexte Garage actif';
      } else {
        eyebrow.textContent = 'Contexte AW';
        title.textContent = 'Aucune réplique Garage active';
        meta.textContent = 'Choisissez une réplique dans Mon Atelier pour conserver le même contexte entre Garage, Compatibility et Atelier.';
      }
      copy.append(eyebrow, title, meta);
      const actions = document.createElement('div');
      actions.className = 'aw-ecosystem-p1-context__actions';
      actions.append(
        buildLink(replica ? 'Garage' : 'Choisir une réplique', URLS.garage, 'aw-ecosystem-p1-link'),
        buildLink('Compatibilité', compatibilityUrl(replica), 'aw-ecosystem-p1-link'),
        buildLink('Atelier', URLS.atelier, 'aw-ecosystem-p1-link')
      );
      strip.append(copy, actions);
    });
  };

  const serviceHandleFromLink = (link) => {
    try {
      const path = new URL(link.href, location.origin).pathname;
      return String(path.match(/^\/products\/([^/?#]+)/i)?.[1] || '').trim();
    } catch (_) { return ''; }
  };
  const serviceCardByHandle = (root, handle) => {
    const wanted = normalize(handle);
    if (!wanted) return null;
    const link = Array.from(root.querySelectorAll('a[href*="/products/"]'))
      .find((item) => normalize(serviceHandleFromLink(item)) === wanted);
    return link?.closest('.aw14-card') || null;
  };
  const addServiceNote = (card, text, exact = false) => {
    if (!card) return false;
    card.classList.add('is-aw-active-service');
    card.dataset.awWorkshopMatch = exact ? 'exact' : 'type';
    const note = document.createElement('p');
    note.dataset.awActiveServiceNote = 'true';
    note.className = 'aw-ecosystem-p1-service-note';
    note.textContent = text;
    const footer = card.querySelector('.aw14-card__footer');
    if (footer) footer.before(note);
    else card.append(note);
    return true;
  };
  const enhanceAtelier = () => {
    const root = document.querySelector('#aw-atelier-hub');
    if (!root) return;
    root.querySelectorAll('.aw14-card.is-aw-active-service').forEach((card) => {
      card.classList.remove('is-aw-active-service');
      delete card.dataset.awWorkshopMatch;
    });
    root.querySelectorAll('[data-aw-active-service-note]').forEach((note) => note.remove());
    const replica = activeReplica();
    if (!replica) return;

    const record = resolveExactModel(replica);
    let exactCount = 0;
    if (record?.workshopServices?.length) {
      record.workshopServices.forEach((handle) => {
        const card = serviceCardByHandle(root, handle);
        if (!card) return;
        const applied = addServiceNote(
          card,
          'Relation Atelier enregistrée pour ce modèle exact dans Shopify. Le périmètre de la prestation et la configuration réelle restent à vérifier avant intervention.',
          true
        );
        if (applied) exactCount += 1;
      });
    }
    if (exactCount > 0) return;

    if (normalize(replica.type) !== 'aeg') return;
    const card = serviceCardByHandle(root, 'forfait-revision-aeg');
    addServiceNote(
      card,
      'Type AEG déclaré dans le Garage : cette prestation est conçue pour les AEG. La version exacte et le périmètre restent à vérifier avant intervention.',
      false
    );
  };

  const enhanceGarage = () => {
    const currentId = activeId();
    document.querySelectorAll('[data-aw-replica-edit]').forEach((editButton) => {
      const id = String(editButton.dataset.awReplicaEdit || '').trim();
      if (!id) return;
      const card = editButton.closest('.aw-p2-card') || editButton.closest('[data-aw-replica-card]');
      const actions = editButton.closest('.aw-p2-card__actions') || editButton.parentElement;
      if (!actions) return;
      if (card) card.classList.toggle('is-aw-active-replica', id === currentId);
      let activateButton = actions.querySelector(`[data-aw-use-replica="${id}"]`);
      if (!activateButton) {
        activateButton = document.createElement('button');
        activateButton.type = 'button';
        activateButton.className = 'aw-ecosystem-p1-use';
        activateButton.dataset.awUseReplica = id;
        actions.append(activateButton);
      }
      const isActive = id === currentId;
      activateButton.textContent = isActive ? 'Réplique active ✓' : 'Utiliser cette réplique';
      activateButton.disabled = isActive;
      activateButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      const replica = replicas().find((item) => String(item?.id || '') === id) || null;
      let compatibility = actions.querySelector(`[data-aw-go-compatibility="${id}"]`);
      if (!compatibility) {
        compatibility = buildLink('Vérifier la compatibilité', compatibilityUrl(replica), 'aw-ecosystem-p1-card-link');
        compatibility.dataset.awGoCompatibility = id;
        actions.append(compatibility);
      } else {
        compatibility.href = compatibilityUrl(replica);
      }
      if (!actions.querySelector(`[data-aw-go-atelier="${id}"]`)) {
        const atelier = buildLink('Consulter l’Atelier', URLS.atelier, 'aw-ecosystem-p1-card-link');
        atelier.dataset.awGoAtelier = id;
        actions.append(atelier);
      }
    });
  };

  const render = () => {
    injectCompatibilityModels();
    renderCompatibilityCoverage();
    renderContextStrip();
    enhanceGarage();
    enhanceAtelier();
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-aw-use-replica],[data-aw-go-compatibility],[data-aw-go-atelier]');
    if (!control) return;
    const id = control.dataset.awUseReplica || control.dataset.awGoCompatibility || control.dataset.awGoAtelier || '';
    const replica = activate(id, 'garage-active-user');
    if (!replica && control.tagName === 'A') event.preventDefault();
  });
  window.addEventListener('aw:p2-garage-updated', () => setTimeout(() => {
    const replica = activeReplica();
    if (replica) syncReplica(replica, 'garage-active-update');
    render();
  }, 0));
  window.addEventListener('storage', (event) => {
    if (![KEYS.active, KEYS.replicas].includes(event.key)) return;
    const replica = activeReplica();
    if (replica) syncReplica(replica, 'garage-active-storage');
    render();
  });
  window.addEventListener('aw:context-changed', () => queueMicrotask(() => {
    renderContextStrip();
    enhanceAtelier();
  }));
  document.addEventListener('shopify:section:load', () => setTimeout(render, 0));

  window.AWEcosystemP1Bridge = {
    version: '1.3.0',
    keys: { replicas: KEYS.replicas, active: KEYS.active, context: KEYS.context },
    activeReplica,
    resolveExactModel,
    compatibilityUrl,
    injectCompatibilityModels,
    renderCompatibilityCoverage,
    enhanceAtelier,
    activate,
    syncActive,
    enhanceGarage,
    renderContextStrip
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', render, { once: true });
  else render();
})();